import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api";
import HRHeader from "../components/HRHeader";
import InteractiveTour from "../components/InteractiveTour";
import { AdminBalancesWidget } from "../components/AdminBalancesWidget";
import { toast } from "sonner";
import { seniorityLabel } from "./admin/adminUtils";
import { buildNav, accessibleItems, resolveKeys } from "../nav";
import { usePendingApprovals } from "../hooks/usePendingApprovals";
import {
  Megaphone, ChevronRight, Sparkles, Award, Car, Leaf, AlertTriangle, ChevronDown, TrendingUp, TrendingDown, Minus, SlidersHorizontal, Check, X
} from "lucide-react";

const PortalPage = () => {
  const { user, isAdmin, isApprover, isExternal, isEmployee, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [news, setNews] = useState([]);
  const [showTour, setShowTour] = useState(false);
  const [myCar, setMyCar] = useState(null);
  const [kmInput, setKmInput] = useState("");
  const [kmSaving, setKmSaving] = useState(false);
  const [showCarHistory, setShowCarHistory] = useState(false);
  const [editingFav, setEditingFav] = useState(false);
  const [draftFav, setDraftFav] = useState([]);
  const [savingFav, setSavingFav] = useState(false);
  const { count: pendingApprovals } = usePendingApprovals(isApprover);
  const firstName = (user?.name || "").split(" ")[0];
  const seniority = user?.hire_date ? seniorityLabel(user.hire_date) : null;
  const hireFmt = (() => {
    try { return new Date(user.hire_date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }); }
    catch { return ""; }
  })();

  useEffect(() => {
    api.get("/auth/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/announcements").then((r) => setAnnouncements(r.data.slice(0, 4))).catch(() => {});
    api.get("/changelog").then((r) => setNews((r.data?.entries || []).slice(0, 3))).catch(() => {});
    api.get("/my-car").then((r) => { const car = r.data?.car || null; setMyCar(car); setKmInput(car?.km != null ? String(car.km) : ""); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    const key = `tb_tour_done_${user.id}`;
    if (localStorage.getItem("tb_tour_replay") === "1") {
      localStorage.removeItem("tb_tour_replay");
      setShowTour(true);
    } else if (!localStorage.getItem(key)) {
      setShowTour(true);
    }
  }, [user]);

  useEffect(() => {
    const onReplay = () => setShowTour(true);
    window.addEventListener("tb-replay-tour", onReplay);
    return () => window.removeEventListener("tb-replay-tour", onReplay);
  }, []);

  const closeTour = () => {
    if (user?.id) localStorage.setItem(`tb_tour_done_${user.id}`, "1");
    setShowTour(false);
  };

  const saveKm = async () => {
    const val = Number(kmInput);
    if (kmInput === "" || Number.isNaN(val) || val < 0) { toast.error("Inserisci un valore di km valido"); return; }
    setKmSaving(true);
    try {
      await api.post("/my-car/km", { km: val });
      const r = await api.get("/my-car");
      const car = r.data?.car || null;
      setMyCar(car); setKmInput(car?.km != null ? String(car.km) : "");
      toast.success("Chilometri aggiornati");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setKmSaving(false); }
  };

  const { quick, macros } = buildNav({ isAdmin, isApprover, isExternal, isEmployee });
  const flags = { isAdmin, isApprover, isExternal, isEmployee };
  const favOptions = accessibleItems(flags).filter((i) => i.key !== "presenze");
  const defaultFavKeys = quick.filter((q) => q.key !== "presenze").map((q) => q.key);
  const savedFavKeys = Array.isArray(user?.quick_favorites) ? user.quick_favorites : null;
  const activeFavKeys = savedFavKeys && savedFavKeys.length > 0 ? savedFavKeys : defaultFavKeys;
  const quickCards = resolveKeys(activeFavKeys, flags).filter((i) => i.key !== "presenze");

  const startEditFav = () => { setDraftFav(activeFavKeys); setEditingFav(true); };
  const toggleFav = (key) => setDraftFav((d) => (d.includes(key) ? d.filter((k) => k !== key) : [...d, key]));
  const persistFav = async (keys, okMsg) => {
    setSavingFav(true);
    try {
      await api.put("/profile/favorites", { favorites: keys });
      await refreshUser();
      toast.success(okMsg);
      setEditingFav(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSavingFav(false); }
  };
  const saveFav = () => {
    const ordered = favOptions.map((o) => o.key).filter((k) => draftFav.includes(k));
    persistFav(ordered, "Accessi rapidi aggiornati");
  };
  const resetFav = () => persistFav([], "Ripristinati gli accessi rapidi predefiniti");

  const go = (it) => {
    if (it.external && it.href) window.open(it.href, "_blank", "noopener,noreferrer");
    else if (it.to) navigate(it.to);
  };

  const ago = (iso) => {
    try {
      const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
      if (days <= 0) return "Oggi";
      if (days === 1) return "Ieri";
      return `${days} giorni fa`;
    } catch { return ""; }
  };

  const riepilogo = [
    { l: "Ferie residue", v: summary ? `${summary.ferie_balance} giorni` : "..." },
    { l: "Permessi (ROL)", v: summary ? `${summary.rol_balance} ore` : "..." },
    { l: "Ex festività", v: summary ? `${summary.exfestivita_balance ?? 0} ore` : "..." },
    { l: "Permesso studio", v: summary ? `${summary.studio_balance ?? 0} ore` : "..." },
    { l: "Richieste in attesa", v: summary ? `${summary.leaves_pending}` : "..." }
  ];

  return (
    <div className="min-h-screen bg-[#f0f2f5]">
      {showTour && <InteractiveTour isAdmin={isAdmin} isApprover={isApprover} isExternal={isExternal} onClose={closeTour} />}
      <HRHeader />

      <section className="bg-gradient-to-br from-[#16345c] to-[#0d1b2f] text-white">
        <div className="max-w-[1200px] mx-auto px-4 py-12 md:py-16">
          <span className="inline-flex items-center gap-2 bg-white/10 text-[12px] font-semibold px-3 py-1 rounded-full mb-4">
            Ciao {firstName}, bentornato
            {isExternal && <span className="text-[10px] font-bold uppercase bg-orange-400/90 text-white px-1.5 py-0.5 rounded-full">Collaboratore esterno</span>}
          </span>
          <h1 className="text-[34px] md:text-[46px] font-black leading-tight mb-3">Teleborsa HR</h1>
          <p className="text-[16px] md:text-[19px] text-gray-300 max-w-2xl leading-relaxed mb-2">
            Piattaforma dedicata ai dipendenti delle aziende appartenenti al gruppo Teleborsa: <span className="text-white font-semibold">Teleborsa srl</span> e <span className="text-white font-semibold">S.I.T. srl</span>. Buste paga, ferie, comunicazioni e servizi HR.
          </p>
        </div>
      </section>

      <main className="max-w-[1200px] mx-auto px-4 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">

          <div className="mb-8" data-testid="home-quick-access">
            <div className="flex items-center gap-3 mb-3">
              <h2 className="text-[#16345c] text-[15px] font-black uppercase tracking-tight whitespace-nowrap">Accesso rapido</h2>
              <div className="flex-1 h-px bg-gray-200" />
              {!editingFav ? (
                <button onClick={startEditFav} data-testid="home-quick-customize" className="flex items-center gap-1.5 text-[12px] font-bold text-[#16345c] hover:text-[#e30613] transition-colors shrink-0">
                  <SlidersHorizontal size={14} /> Personalizza
                </button>
              ) : (
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={resetFav} disabled={savingFav} data-testid="home-quick-reset" className="text-[12px] font-semibold text-gray-500 hover:text-[#16345c] transition-colors">Predefiniti</button>
                  <button onClick={() => setEditingFav(false)} disabled={savingFav} data-testid="home-quick-cancel" className="flex items-center gap-1 text-[12px] font-bold text-gray-600 border border-gray-300 rounded-sm px-2.5 py-1 hover:bg-gray-100 transition-colors"><X size={13} /> Annulla</button>
                  <button onClick={saveFav} disabled={savingFav} data-testid="home-quick-save" className="flex items-center gap-1 text-[12px] font-bold text-white bg-[#009640] hover:bg-[#007a34] rounded-sm px-2.5 py-1 transition-colors disabled:opacity-60"><Check size={13} /> Salva</button>
                </div>
              )}
            </div>

            {editingFav ? (
              <div className="bg-white border-2 border-dashed border-[#16345c]/25 rounded-lg p-4" data-testid="home-quick-editor">
                <p className="text-[12.5px] text-gray-500 mb-3">Seleziona le funzioni da mostrare tra gli accessi rapidi. Le preferenze ti seguono su ogni dispositivo.</p>
                <div className="flex flex-wrap gap-2">
                  {favOptions.map((o) => {
                    const on = draftFav.includes(o.key);
                    return (
                      <button key={o.key} onClick={() => toggleFav(o.key)} data-testid={`home-quick-opt-${o.key}`}
                        className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] font-semibold transition-all ${on ? "border-[#16345c] bg-[#16345c] text-white" : "border-gray-300 bg-white text-gray-600 hover:border-[#16345c]"}`}>
                        <o.icon size={14} style={on ? undefined : { color: o.color }} /> {o.label}
                        {on && <Check size={13} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : quickCards.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {quickCards.map((q) => (
                  <button key={q.key} onClick={() => go(q)} data-testid={`home-quick-${q.key}`}
                    className="group relative bg-gradient-to-br from-white to-[#eef3f9] border-2 border-[#16345c]/15 rounded-lg p-5 text-left hover:shadow-lg hover:border-[#16345c] transition-all focus:outline-none focus:ring-2 focus:ring-[#ff7e00]">
                    {q.key === "approvazioni" && pendingApprovals > 0 && (
                      <span data-testid="home-quick-approvazioni-badge" className="absolute top-3 right-3 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#e30613] text-white text-[11px] font-black">{pendingApprovals > 99 ? "99+" : pendingApprovals}</span>
                    )}
                    <span className="w-12 h-12 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${q.color}1a`, color: q.color }}>
                      <q.icon size={24} />
                    </span>
                    <div className="text-[15px] font-black text-[#16345c] flex items-center gap-1">
                      {q.label}
                      <ChevronRight size={15} className="text-gray-400 group-hover:translate-x-0.5 group-hover:text-[#e30613] transition-all" />
                    </div>
                    <div className="text-[12.5px] text-gray-500 mt-0.5">{q.desc}</div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-dashed border-gray-300 rounded-lg p-6 text-center text-[13px] text-gray-400" data-testid="home-quick-empty">
                Nessun accesso rapido selezionato. Usa "Personalizza" per aggiungerne.
              </div>
            )}
          </div>

          <div className="space-y-8">
            {macros.map((grp) => (
              <div key={grp.key} data-testid={`home-macro-${grp.key}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-8 h-8 rounded-md bg-[#16345c]/10 text-[#16345c] flex items-center justify-center"><grp.icon size={18} /></span>
                  <h3 className="text-[14px] font-black text-[#16345c] uppercase tracking-tight whitespace-nowrap">{grp.label}</h3>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {grp.items.map((q) => (
                    <button key={q.key} onClick={() => go(q)} data-testid={`home-card-${q.key}`}
                      className="group relative bg-white border border-gray-200 rounded-lg p-5 text-left hover:shadow-lg hover:border-[#16345c] transition-all focus:outline-none focus:ring-2 focus:ring-[#ff7e00]">
                      <span className="w-11 h-11 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${q.color}1a`, color: q.color }}>
                        <q.icon size={22} />
                      </span>
                      <div className="text-[15px] font-bold text-[#16345c] flex items-center gap-1">
                        {q.label}
                        <ChevronRight size={15} className="text-gray-400 group-hover:translate-x-0.5 group-hover:text-[#e30613] transition-all" />
                      </div>
                      <div className="text-[12.5px] text-gray-500 mt-0.5">{q.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <aside>
          <h2 className="text-[#16345c] text-[19px] font-black uppercase tracking-tight mb-4 flex items-center gap-2">
            <Megaphone size={18} /> Comunicazioni
          </h2>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {announcements.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-400 text-[13px]">Nessuna comunicazione</div>
            ) : announcements.map((a) => (
              <button key={a.id} onClick={() => navigate("/comunicazioni")} className="w-full text-left block px-4 py-4 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors group">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-black text-[#e30613] uppercase tracking-wide">{a.tag}</span>
                  <span className="text-[11px] text-gray-400">{ago(a.created_at)}</span>
                </div>
                <p className="text-[14px] font-semibold text-gray-800 leading-snug group-hover:text-[#16345c] transition-colors">{a.title}</p>
              </button>
            ))}
          </div>

          {!isExternal && (
            <div className="mt-5 bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="text-[14px] font-bold text-[#16345c] mb-3">Il mio riepilogo</h3>
              <div className="space-y-3">
                {riepilogo.map((s) => (
                  <div key={s.l} className="flex items-center justify-between text-[13px]">
                    <span className="text-gray-500">{s.l}</span>
                    <span className="font-bold text-[#16345c]">{s.v}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => navigate("/ferie")} className="w-full mt-4 bg-[#16345c] hover:bg-[#0d1b2f] transition-colors text-white text-[13px] font-bold py-2.5 rounded-sm">
                Richiedi ferie o permessi
              </button>
            </div>
          )}
          {isAdmin && <AdminBalancesWidget />}
          {seniority && seniority !== "—" && !isEmployee && (
            <div className="mt-5 bg-white border border-gray-200 rounded-lg p-5" data-testid="home-seniority">
              <h3 className="text-[14px] font-bold text-[#16345c] mb-3 flex items-center gap-2"><Award size={16} className="text-[#009640]" /> {isExternal ? "Anzianità di collaborazione" : "La mia anzianità"}</h3>
              <div className="text-[26px] font-black text-[#16345c] leading-none" data-testid="home-seniority-value">{seniority}</div>
              {hireFmt && (
                <div className="text-[12px] text-gray-500 mt-2">{isExternal ? "Inizio collaborazione" : "In azienda dal"} {hireFmt}</div>
              )}
            </div>
          )}
          {myCar && !isEmployee && (
            <div className="mt-5 bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="home-car-km">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-[#0f5132] to-[#157347]">
                <Leaf size={16} className="text-white" />
                <h3 className="text-[13px] font-bold text-white flex-1">Auto aziendale — Chilometri (ESG)</h3>
                <Car size={16} className="text-white/80" />
              </div>
              <div className="p-4">
                <div className="text-[13px] text-gray-700 mb-3">
                  <span className="font-semibold text-[#16345c]">{myCar.car_model || "Auto aziendale"}</span>
                  {myCar.car_plate && <span className="ml-1 text-gray-500">· {myCar.car_plate}</span>}
                </div>
                {myCar.needs_update ? (
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-[12.5px] px-3 py-2 rounded-sm mb-3" data-testid="home-car-km-alert">
                    <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                    <span>Non hai ancora inserito i chilometri per il <b>{myCar.year}</b>. Aggiorna il dato entro il <b>31 dicembre</b>.</span>
                  </div>
                ) : (
                  <div className="text-[12px] text-gray-500 mb-3">Ultimo aggiornamento: {myCar.updated_at ? new Date(myCar.updated_at).toLocaleDateString("it-IT") : "—"}</div>
                )}
                <label className="block text-[12px] font-semibold text-gray-600 mb-1">Km percorsi nell'anno {myCar.year}</label>
                {myCar.km != null && (
                  <div className="text-[12px] text-gray-500 mb-1" data-testid="home-car-km-saved">Valore registrato: <b className="text-[#157347]">{Number(myCar.km).toLocaleString("it-IT")} km</b></div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="number" min="0" value={kmInput}
                    onChange={(e) => setKmInput(e.target.value)}
                    data-testid="home-car-km-input"
                    placeholder="es. 15000"
                    className="flex-1 border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#157347]"
                  />
                  <span className="text-[13px] text-gray-500">km</span>
                </div>
                <button onClick={saveKm} disabled={kmSaving} data-testid="home-car-km-save" className="w-full mt-3 bg-[#157347] hover:bg-[#0f5132] transition-colors text-white text-[13px] font-bold py-2.5 rounded-sm disabled:opacity-60">
                  {kmSaving ? "Salvataggio..." : "Salva chilometri"}
                </button>
                <p className="text-[11px] text-gray-400 mt-2 leading-relaxed">Il dato viene usato per la rendicontazione di sostenibilità (ESG) del gruppo. Ricordati di aggiornarlo al 31 dicembre di ogni anno.</p>
                {myCar.history && myCar.history.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <button onClick={() => setShowCarHistory((v) => !v)} data-testid="home-car-km-history-toggle" className="w-full flex items-center justify-between text-[12px] font-semibold text-[#16345c] hover:text-[#157347] transition-colors">
                      <span>Storico chilometri ({myCar.history.length} {myCar.history.length === 1 ? "anno" : "anni"})</span>
                      <ChevronDown size={15} className={`transition-transform ${showCarHistory ? "rotate-180" : ""}`} />
                    </button>
                    {showCarHistory && (
                      <ul className="mt-2 space-y-1.5" data-testid="home-car-km-history">
                        {myCar.history.map((hh) => (
                          <li key={hh.year} className="flex items-center justify-between text-[12.5px]">
                            <span className="text-gray-500 font-medium">{hh.year}</span>
                            <span className="flex items-center gap-2">
                              <span className="font-semibold text-gray-700">{Number(hh.km).toLocaleString("it-IT")} km</span>
                              {hh.delta == null ? (
                                <span className="inline-flex items-center gap-0.5 text-gray-300 w-[74px] justify-end"><Minus size={12} /></span>
                              ) : hh.delta >= 0 ? (
                                <span className="inline-flex items-center gap-0.5 text-emerald-600 w-[74px] justify-end"><TrendingUp size={12} /> +{Number(hh.delta).toLocaleString("it-IT")}</span>
                              ) : (
                                <span className="inline-flex items-center gap-0.5 text-red-600 w-[74px] justify-end"><TrendingDown size={12} /> {Number(hh.delta).toLocaleString("it-IT")}</span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          {news.length > 0 && (
            <div className="mt-5 bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="home-news">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-[#16345c] to-[#0d1b2f]">
                <h3 className="text-[13px] font-bold text-white flex items-center gap-1.5"><Sparkles size={15} /> Novità del portale</h3>
                <button onClick={() => navigate("/novita")} data-testid="home-news-all" className="text-[11px] font-semibold text-white/80 hover:text-white transition-colors">Vedi tutte</button>
              </div>
              {news.map((n) => (
                <button key={n.id} onClick={() => navigate("/novita")} className="w-full text-left block px-4 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors group">
                  <div className="flex items-center gap-2 mb-0.5">
                    {n.pinned && <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase text-amber-600" data-testid="home-news-pinned"><Sparkles size={10} /> In evidenza</span>}
                    <span className="text-[10px] font-black text-[#e30613] uppercase tracking-wide">{n.tag}</span>
                    <span className="text-[11px] text-gray-400">{new Date(n.date).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}</span>
                  </div>
                  <p className="text-[13.5px] font-semibold text-gray-800 leading-snug group-hover:text-[#16345c] transition-colors">{n.title}</p>
                </button>
              ))}
            </div>
          )}
        </aside>
      </main>

      <footer className="bg-[#0d1b2f] text-gray-400 mt-6">
        <div className="max-w-[1200px] mx-auto px-4 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <span className="text-[20px] font-black text-white">Tele<span className="text-[#ff7e00]">borsa</span> <span className="font-light text-[#ff7e00]">HR</span></span>
          <p className="text-[11px]">© 2026 Teleborsa S.r.l. - Piattaforma riservata ai dipendenti del gruppo</p>
          <div className="flex gap-4 text-[11px]">
            <a href="#" className="hover:text-white transition-colors">Privacy</a>
            <a href="#" className="hover:text-white transition-colors">Assistenza IT</a>
            <a href="#" className="hover:text-white transition-colors">Regolamento</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PortalPage;
